import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasRole, Role } from '@nexa/auth';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useSession } from '@/auth/use-session';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  useApplyBenefitOverride,
  useBenefitOverrides,
  useDecideBenefitOverride,
} from '@/lib/disruptions/use-benefit-overrides';
import type {
  BenefitOverride,
  BenefitOverrideApplicationOutcome,
  BenefitOverrideStatus,
} from '@/lib/disruptions/types';
import { formatDateTime } from '../labels';

// "Autorizaciones" (#819, SKY PoC A9) — the event's benefit overrides: PROPOSED
// ones wait for a supervisor (Aprobar / Rechazar with a note — never the author:
// cases answers 409 same_actor), APPROVED ones get ONE "Aplicar" for the whole
// event (idempotent), APPLIED ones show the per-PNR result (emitido / top-up /
// adicional / omitido) with the link to the original benefit. Author, authorizer,
// instants and original-vs-new value are always visible; the policy is untouched.

const STATUS_VARIANT: Record<BenefitOverrideStatus, BadgeVariant> = {
  PROPOSED: 'warning',
  APPROVED: 'info',
  REJECTED: 'muted',
  APPLIED: 'success',
};

export const OUTCOME_VARIANT: Record<BenefitOverrideApplicationOutcome, BadgeVariant> = {
  ISSUED: 'info',
  TOPPED_UP: 'success',
  ADDITIONAL: 'success',
  SKIPPED: 'muted',
};

const OUTCOME_KEY: Record<BenefitOverrideApplicationOutcome, string> = {
  ISSUED: 'override.outcome.issued',
  TOPPED_UP: 'override.outcome.toppedUp',
  ADDITIONAL: 'override.outcome.additional',
  SKIPPED: 'override.outcome.skipped',
};

export function outcomeLabelKey(outcome: BenefitOverrideApplicationOutcome): string {
  return OUTCOME_KEY[outcome];
}

function actorLabel(a: { urn: string; name?: string } | undefined): string {
  if (!a) return '—';
  return a.name ?? a.urn.split('|').pop() ?? a.urn;
}

function statusOf(err: unknown): { status: number | undefined } {
  const resp = (err as { response?: { status?: number } })?.response;
  return { status: resp?.status };
}

export function CareBenefitOverridePanel({
  disruptionUrn,
  focusUrn,
  onOpenProposal,
}: {
  disruptionUrn: string;
  /** A row chip asked for this override — expand it. */
  focusUrn?: string | null;
  onOpenProposal: () => void;
}) {
  const { t } = useTranslation('care');
  const { role, operatorUrn } = useSession();
  const supervisor = hasRole(role, Role.OPS_SUPERVISOR);
  const list = useBenefitOverrides(disruptionUrn);
  // A degraded / mis-shaped read must never crash the desk: only an array is a list.
  const items = Array.isArray(list.data) ? list.data : [];
  const pending = items.filter((o) => o.status === 'PROPOSED' || o.status === 'APPROVED').length;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (focusUrn) {
      setOpen(true);
      setExpanded(focusUrn);
    }
  }, [focusUrn]);

  const sorted = useMemo(() => items, [items]);

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="benefit-override-panel">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          data-testid="benefit-override-toggle"
        >
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden />
          <span className="text-sm font-semibold">{t('override.panel.title')}</span>
          {pending > 0 ? (
            <span className="rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold tabular-nums text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" data-testid="benefit-override-pending">
              {pending}
            </span>
          ) : null}
          {list.isLoading ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden /> : null}
        </button>
        <Button size="sm" variant="outline" onClick={onOpenProposal} data-testid="benefit-override-propose">
          {t('override.panel.propose')}
        </Button>
      </div>

      {open ? (
        <div className="border-t border-border px-3 py-2">
          {list.isError ? (
            <p className="text-sm text-muted-foreground">{t('override.panel.error')}</p>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="benefit-override-empty">
              {t('override.panel.empty')}
            </p>
          ) : (
            <ul className="space-y-2">
              {sorted.map((o) => (
                <OverrideRow
                  key={o.urn}
                  o={o}
                  disruptionUrn={disruptionUrn}
                  supervisor={supervisor}
                  isAuthor={o.proposedBy.urn === operatorUrn}
                  expanded={expanded === o.urn}
                  onToggle={() => setExpanded((cur) => (cur === o.urn ? null : o.urn))}
                />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}

function OverrideRow({
  o,
  disruptionUrn,
  supervisor,
  isAuthor,
  expanded,
  onToggle,
}: {
  o: BenefitOverride;
  disruptionUrn: string;
  supervisor: boolean;
  isAuthor: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('care');
  const decide = useDecideBenefitOverride(disruptionUrn);
  const apply = useApplyBenefitOverride(disruptionUrn);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const short = o.urn.split(':').pop() ?? o.urn;
  const summary = useMemo(() => {
    const counts: Record<BenefitOverrideApplicationOutcome, number> = { ISSUED: 0, TOPPED_UP: 0, ADDITIONAL: 0, SKIPPED: 0 };
    for (const r of o.application ?? []) counts[r.outcome] += 1;
    return counts;
  }, [o.application]);

  const onDecide = async (decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    setError(null);
    try {
      await decide.mutateAsync({ overrideUrn: o.urn, decision, ...(note.trim() ? { note: note.trim() } : {}) });
      setNote('');
    } catch (err) {
      const { status } = statusOf(err);
      setError(status === 409 ? t('override.panel.errors.sameActor') : status === 403 ? t('override.panel.errors.forbidden') : t('override.panel.errors.generic'));
    }
  };

  const onApply = async (): Promise<void> => {
    setError(null);
    try {
      await apply.mutateAsync({ overrideUrn: o.urn });
    } catch (err) {
      const { status } = statusOf(err);
      setError(status === 409 ? t('override.panel.errors.notApproved') : status === 403 ? t('override.panel.errors.forbidden') : t('override.panel.errors.generic'));
    }
  };

  const busy = decide.isPending || apply.isPending;

  return (
    <li className="rounded-md border border-border" data-testid={`benefit-override-${short}`}>
      <button type="button" onClick={onToggle} aria-expanded={expanded} className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left">
        <Badge variant={STATUS_VARIANT[o.status]} size="sm" data-testid="benefit-override-status">
          {t(`override.status.${o.status.toLowerCase()}`)}
        </Badge>
        <span className="text-sm font-semibold tabular-nums">
          {t('override.panel.valueLine', {
            from: o.proposedByPolicy.amountUsd,
            to: o.override.amountUsd,
            currency: o.override.currency,
          })}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {t('override.panel.byLine', { who: actorLabel(o.proposedBy), when: formatDateTime(o.proposedBy.at) })}
        </span>
        {o.status === 'APPLIED' ? (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground" data-testid="benefit-override-summary">
            {t('override.panel.summary', summary)}
          </span>
        ) : null}
      </button>

      {expanded ? (
        <div className="space-y-2 border-t border-border px-2.5 py-2 text-sm">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted-foreground">{t('override.panel.reason')}</dt>
            <dd>{o.reason}</dd>
            <dt className="text-muted-foreground">{t('override.panel.policy')}</dt>
            <dd className="truncate" title={o.policyUrn}>
              {o.policyUrn ? o.policyUrn.split(':').pop() : '—'} · {t('override.panel.policyUntouched')}
            </dd>
            <dt className="text-muted-foreground">{t('override.panel.proposedBy')}</dt>
            <dd>{actorLabel(o.proposedBy)} · {formatDateTime(o.proposedBy.at)}</dd>
            {o.decidedBy ? (
              <>
                <dt className="text-muted-foreground">
                  {o.status === 'REJECTED' ? t('override.panel.rejectedBy') : t('override.panel.approvedBy')}
                </dt>
                <dd data-testid="benefit-override-decided-by">
                  {actorLabel(o.decidedBy)} · {formatDateTime(o.decidedBy.at)}
                  {o.decidedBy.note ? <span className="text-muted-foreground"> · {o.decidedBy.note}</span> : null}
                </dd>
              </>
            ) : null}
            {o.appliedAt ? (
              <>
                <dt className="text-muted-foreground">{t('override.panel.appliedAt')}</dt>
                <dd>{formatDateTime(o.appliedAt)}{o.appliedBy ? ` · ${actorLabel(o.appliedBy)}` : ''}</dd>
              </>
            ) : null}
          </dl>

          {o.status === 'PROPOSED' ? (
            supervisor ? (
              <div className="space-y-1.5" data-testid="benefit-override-decide">
                {isAuthor ? (
                  <p className="text-xs text-amber-800 dark:text-amber-200" data-testid="benefit-override-author-warning">
                    {t('override.panel.authorCannotDecide')}
                  </p>
                ) : null}
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('override.panel.notePlaceholder')}
                  className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                  data-testid="benefit-override-note"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => void onDecide('APPROVE')} disabled={busy || isAuthor} data-testid="benefit-override-approve">
                    {t('override.panel.approve')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void onDecide('REJECT')} disabled={busy || isAuthor} data-testid="benefit-override-reject">
                    {t('override.panel.reject')}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="benefit-override-awaiting">
                {t('override.panel.awaitingSupervisor')}
              </p>
            )
          ) : null}

          {o.status === 'APPROVED' ? (
            supervisor ? (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => void onApply()} disabled={busy} data-testid="benefit-override-apply">
                  {apply.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
                  {t('override.panel.apply')}
                </Button>
                <span className="text-xs text-muted-foreground">{t('override.panel.applyHint')}</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t('override.panel.awaitingApply')}</p>
            )
          ) : null}

          {o.status === 'APPLIED' && o.application ? (
            <div data-testid="benefit-override-application">
              <p className="text-xs font-medium">{t('override.panel.summary', summary)}</p>
              <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto text-xs">
                {o.application.map((r) => (
                  <li key={r.subCaseUrn} className="flex items-center gap-2" data-testid={`benefit-override-row-${r.pnrLabel}`}>
                    <span className="font-mono font-semibold">{r.pnrLabel}</span>
                    <Badge variant={OUTCOME_VARIANT[r.outcome]} size="sm">{t(OUTCOME_KEY[r.outcome])}</Badge>
                    {r.amountUsd > 0 ? <span className="tabular-nums">+{r.amountUsd} {o.override.currency}</span> : null}
                    {r.linkedCardUrn ? (
                      <span className="truncate text-muted-foreground" title={r.linkedCardUrn}>
                        {t('override.panel.linkedCard', { card: r.linkedCardUrn.split(':').pop() })}
                      </span>
                    ) : null}
                    {r.outcome === 'SKIPPED' && r.note ? (
                      <span className="text-muted-foreground">{t(`override.skipReason.${r.note}`, { defaultValue: r.note })}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {error ? (
            <p role="alert" className="text-xs text-rose-700 dark:text-rose-300" data-testid="benefit-override-row-error">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
